import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './TeacherDashboard.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002/api';

const FALLBACK_STANDARDS = [
  '1st', '2nd', '3rd', '4th', '5th', '6th',
  '7th', '8th', '9th', '10th', '11th', '12th'
];
const FALLBACK_DIVISIONS = ['A', 'B', 'C', 'D', 'E'];

const TeacherDashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('assign');
  const [students, setStudents] = useState([]);
  const [devices, setDevices] = useState([]);
  const [classPairs, setClassPairs] = useState([]);
  const [standards, setStandards] = useState(FALLBACK_STANDARDS);
  const [divisions, setDivisions] = useState(FALLBACK_DIVISIONS);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [assignForm, setAssignForm] = useState({ deviceId: '', studentId: '' });
  const [classFilters, setClassFilters] = useState({
    standard: '',
    division: '',
    search: ''
  });

  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }), []);

  const loadLookups = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/school/class-options`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        const pairs = Array.isArray(data.classes) ? data.classes : [];
        setClassPairs(pairs);
        if (Array.isArray(data.standards) && data.standards.length) {
          setStandards(data.standards);
        }
        if (Array.isArray(data.divisions) && data.divisions.length) {
          setDivisions(data.divisions);
        }
        return;
      }
    } catch {
      // fall through to static lists
    }

    try {
      const [stdRes, divRes] = await Promise.all([
        fetch(`${API_BASE_URL}/school/standards`, { headers: authHeaders() }),
        fetch(`${API_BASE_URL}/school/divisions`, { headers: authHeaders() })
      ]);
      if (stdRes.ok) {
        const data = await stdRes.json();
        const names = (Array.isArray(data) ? data : []).map((s) => s.name || s).filter(Boolean);
        if (names.length) setStandards(names);
      }
      if (divRes.ok) {
        const data = await divRes.json();
        const names = (Array.isArray(data) ? data : []).map((d) => d.name || d).filter(Boolean);
        if (names.length) setDivisions(names);
      }
    } catch {
      // keep fallbacks
    }
  }, [authHeaders]);

  const loadDevices = useCallback(async () => {
    const res = await fetch(`${API_BASE_URL}/school/devices`, { headers: authHeaders() });
    if (!res.ok) throw new Error('Failed to load devices');
    const data = await res.json();
    setDevices(Array.isArray(data) ? data : []);
  }, [authHeaders]);

  const loadStudents = useCallback(async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.standard) params.append('standard', filters.standard);
    if (filters.division) params.append('division', filters.division);
    if (filters.search) params.append('search', filters.search);
    const res = await fetch(`${API_BASE_URL}/school/students?${params}`, { headers: authHeaders() });
    if (!res.ok) throw new Error('Failed to load students');
    const data = await res.json();
    setStudents(Array.isArray(data) ? data : []);
    return Array.isArray(data) ? data : [];
  }, [authHeaders]);

  useEffect(() => {
    const boot = async () => {
      setLoading(true);
      setError('');
      try {
        await Promise.all([loadLookups(), loadDevices(), loadStudents({})]);
      } catch (e) {
        setError(e.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    boot();
  }, [loadLookups, loadDevices, loadStudents]);

  const divisionsForStandard = useMemo(() => {
    if (!classFilters.standard) {
      return divisions;
    }
    if (!classPairs.length) return divisions;
    const matched = classPairs
      .filter((c) => String(c.standard).toLowerCase() === String(classFilters.standard).toLowerCase())
      .map((c) => c.division);
    return matched.length ? [...new Set(matched)] : divisions;
  }, [classFilters.standard, classPairs, divisions]);

  const applyClassFilter = async () => {
    setLoading(true);
    setError('');
    setMessage('');
    setAssignForm({ deviceId: '', studentId: '' });
    try {
      const list = await loadStudents({
        standard: classFilters.standard,
        division: classFilters.division,
        search: classFilters.search
      });
      await loadDevices();
      if (!list.length) {
        setError(
          classFilters.standard || classFilters.division
            ? `No students found for ${classFilters.standard || 'any standard'}${classFilters.division ? `-${classFilters.division}` : ''}. Try another class or clear filters.`
            : 'No students found for this school'
        );
      }
    } catch (e) {
      setError(e.message || 'Failed to load class');
    } finally {
      setLoading(false);
    }
  };

  const selectClassPair = (value) => {
    if (!value) {
      setClassFilters((p) => ({ ...p, standard: '', division: '' }));
      return;
    }
    const [standard, ...rest] = value.split('||');
    setClassFilters((p) => ({
      ...p,
      standard: standard || '',
      division: rest.join('||') || ''
    }));
  };

  const selectedPairValue =
    classFilters.standard && classFilters.division
      ? `${classFilters.standard}||${classFilters.division}`
      : '';

  const unassignedDevices = useMemo(
    () =>
      devices.filter((d) => {
        if (Number(d.is_assigned) === 1) return false;
        const id = d.device_id || d.deviceId || d.id;
        return !students.some((s) => String(s.device_id) === String(id));
      }),
    [devices, students]
  );

  const assignableStudents = useMemo(
    () => students.filter((s) => !s.device_id),
    [students]
  );

  const handleAssign = async () => {
    if (!assignForm.deviceId || !assignForm.studentId) {
      setError('Select both device and student');
      return;
    }
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch(`${API_BASE_URL}/school/assign-device`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          device_id: assignForm.deviceId,
          student_id: Number(assignForm.studentId)
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Assign failed');
      setMessage('Device assigned successfully');
      setAssignForm({ deviceId: '', studentId: '' });
      await loadStudents({
        standard: classFilters.standard,
        division: classFilters.division,
        search: classFilters.search
      });
      await loadDevices();
    } catch (e) {
      setError(e.message || 'Assign failed');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusLoad = async () => {
    setLoading(true);
    setError('');
    try {
      const list = await loadStudents(classFilters);
      if (!list.length) {
        setError('No students match these filters');
      }
    } catch (e) {
      setError(e.message || 'Failed to load status');
    } finally {
      setLoading(false);
    }
  };

  const openStatus = () => {
    setActiveTab('status');
    handleStatusLoad();
  };

  const classLabel =
    classFilters.standard && classFilters.division
      ? `${classFilters.standard}-${classFilters.division}`
      : classFilters.standard
        ? classFilters.standard
        : 'All classes';

  const handlePrintAssignmentList = () => {
    if (!students.length) {
      setError('No students to print. Apply a class filter first.');
      return;
    }

    setError('');
    const printedAt = new Date().toLocaleString();
    const teacherName = user?.name || user?.email || 'Class Teacher';
    const escapeHtml = (value) =>
      String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const rows = students
      .map((s, index) => {
        const name = `${s.first_name || ''} ${s.last_name || ''}`.trim() || '-';
        const deviceId = s.device_id || 'Not assigned';
        const deviceType = s.device_type || (s.device_id ? 'GPS Tracker' : '-');
        const status = s.device_id ? (s.device_status || 'Assigned') : 'No device';
        return `<tr>
          <td>${index + 1}</td>
          <td>${escapeHtml(name)}</td>
          <td>${escapeHtml(s.roll_number || '-')}</td>
          <td>${escapeHtml(`${s.standard || '-'}-${s.division || '-'}`)}</td>
          <td>${escapeHtml(deviceId)}</td>
          <td>${escapeHtml(deviceType)}</td>
          <td>${escapeHtml(status)}</td>
        </tr>`;
      })
      .join('');

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Device assignment — ${escapeHtml(classLabel)}</title>
  <style>
    body { font-family: Arial, sans-serif; color: #111; margin: 24px; }
    h1 { margin: 0 0 4px; font-size: 18px; }
    .meta { color: #444; font-size: 12px; margin-bottom: 16px; line-height: 1.5; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    th, td { border: 1px solid #ccc; padding: 8px 6px; text-align: left; }
    th { background: #f3f4f6; }
  </style>
</head>
<body>
  <h1>Student Device Assignment List</h1>
  <div class="meta">
    Class: <strong>${escapeHtml(classLabel)}</strong><br />
    Teacher: ${escapeHtml(teacherName)}<br />
    Students: ${students.length}<br />
    Printed: ${escapeHtml(printedAt)}<br />
    Forward this list to school admin for payment processing.
  </div>
  <table>
    <thead>
      <tr>
        <th>#</th>
        <th>Student name</th>
        <th>Roll</th>
        <th>Class</th>
        <th>Device ID</th>
        <th>Device type</th>
        <th>Status</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>
</body>
</html>`;

    // Use a hidden iframe — avoids blank popup + false "blocked" when noopener is used.
    const existing = document.getElementById('teacher-print-frame');
    if (existing) existing.remove();

    const iframe = document.createElement('iframe');
    iframe.id = 'teacher-print-frame';
    iframe.title = 'Print assignment list';
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;';
    document.body.appendChild(iframe);

    const frameDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!frameDoc) {
      setError('Unable to prepare print view. Try again.');
      iframe.remove();
      return;
    }

    frameDoc.open();
    frameDoc.write(html);
    frameDoc.close();

    const triggerPrint = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch (_) {
        setError('Print dialog could not be opened. Try again.');
      } finally {
        setTimeout(() => {
          if (iframe.parentNode) iframe.remove();
        }, 1000);
      }
    };

    // Give the iframe a moment to render before printing.
    setTimeout(triggerPrint, 300);
  };

  return (
    <div className="teacher-shell">
      <header className="teacher-topbar">
        <div>
          <h1>Class Teacher</h1>
          <p>{user?.name || user?.email}</p>
        </div>
        <button type="button" className="teacher-logout" onClick={onLogout}>
          Logout
        </button>
      </header>

      <main className="teacher-body">
        <div className="teacher-desktop-tabs">
          <button type="button" className={activeTab === 'assign' ? 'active' : ''} onClick={() => setActiveTab('assign')}>
            Assign Device
          </button>
          <button type="button" className={activeTab === 'status' ? 'active' : ''} onClick={openStatus}>
            Student Status
          </button>
        </div>

        {message && <div className="teacher-alert ok">{message}</div>}
        {error && <div className="teacher-alert err">{error}</div>}
        {loading && <div className="teacher-loading">Loading…</div>}

        <section className="teacher-card teacher-class-picker">
          <div className="teacher-chip">{classLabel} · {students.length} students</div>
          <h2>Choose class</h2>
          <p className="hint">
            Pick a class that already has students in this school, then Apply.
            Leave blank to see all students.
          </p>

          {classPairs.length > 0 && (
            <div className="teacher-filters" style={{ marginBottom: 10 }}>
              <select
                className="full"
                value={selectedPairValue}
                onChange={(e) => selectClassPair(e.target.value)}
                aria-label="Class"
              >
                <option value="">All classes ({classPairs.reduce((n, c) => n + (c.student_count || 0), 0)} students)</option>
                {classPairs.map((c) => (
                  <option key={`${c.standard}-${c.division}`} value={`${c.standard}||${c.division}`}>
                    {c.label || `${c.standard} - ${c.division}`} ({c.student_count})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="teacher-filters">
            <select
              value={classFilters.standard}
              onChange={(e) =>
                setClassFilters((p) => ({
                  ...p,
                  standard: e.target.value,
                  // reset division if it is not valid for new standard
                  division:
                    classPairs.length && e.target.value
                      ? (classPairs.some(
                          (c) =>
                            String(c.standard).toLowerCase() === String(e.target.value).toLowerCase() &&
                            String(c.division).toLowerCase() === String(p.division).toLowerCase()
                        )
                          ? p.division
                          : '')
                      : p.division
                }))
              }
              aria-label="Standard"
            >
              <option value="">All standards</option>
              {standards.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <select
              value={classFilters.division}
              onChange={(e) => setClassFilters((p) => ({ ...p, division: e.target.value }))}
              aria-label="Division"
            >
              <option value="">All divisions</option>
              {divisionsForStandard.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            {activeTab === 'status' && (
              <input
                className="full"
                placeholder="Search name, roll or device"
                value={classFilters.search}
                onChange={(e) => setClassFilters((p) => ({ ...p, search: e.target.value }))}
              />
            )}
            <button
              type="button"
              className="teacher-primary-btn"
              onClick={activeTab === 'status' ? handleStatusLoad : applyClassFilter}
            >
              Apply
            </button>
            <button
              type="button"
              className="teacher-secondary-btn"
              onClick={() => {
                setClassFilters({ standard: '', division: '', search: '' });
                setError('');
                setLoading(true);
                loadStudents({})
                  .then(() => loadDevices())
                  .catch((e) => setError(e.message || 'Failed to load'))
                  .finally(() => setLoading(false));
              }}
            >
              Show all
            </button>
          </div>
        </section>

        {activeTab === 'assign' && (
          <section className="teacher-card">
            <h2>Assign device</h2>
            <p className="hint">Tap a device, then a student without a device, then confirm.</p>

            <div className="teacher-assign-grid">
              <div>
                <div className="teacher-section-title">Available devices ({unassignedDevices.length})</div>
                <div className="teacher-pick-list">
                  {unassignedDevices.map((d) => {
                    const id = d.device_id || d.deviceId || d.id;
                    return (
                      <button
                        type="button"
                        key={id}
                        className={`teacher-pick-item ${assignForm.deviceId === id ? 'selected' : ''}`}
                        onClick={() => setAssignForm((p) => ({ ...p, deviceId: id }))}
                      >
                        <strong>{id}</strong>
                        <span>{d.type || 'GPS device'}</span>
                      </button>
                    );
                  })}
                  {!unassignedDevices.length && <div className="teacher-empty">No free devices right now</div>}
                </div>
              </div>

              <div>
                <div className="teacher-section-title">Students without device ({assignableStudents.length})</div>
                <div className="teacher-pick-list">
                  {assignableStudents.map((s) => (
                    <button
                      type="button"
                      key={s.id}
                      className={`teacher-pick-item ${String(assignForm.studentId) === String(s.id) ? 'selected' : ''}`}
                      onClick={() => setAssignForm((p) => ({ ...p, studentId: String(s.id) }))}
                    >
                      <strong>{s.first_name} {s.last_name}</strong>
                      <span>Roll {s.roll_number || '-'} · {s.standard}-{s.division}</span>
                    </button>
                  ))}
                  {!assignableStudents.length && (
                    <div className="teacher-empty">
                      {students.length
                        ? 'All listed students already have devices'
                        : 'No students loaded — tap Show all or pick a class'}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <button
              type="button"
              className="teacher-primary-btn"
              onClick={handleAssign}
              disabled={loading || !assignForm.deviceId || !assignForm.studentId}
            >
              Confirm assignment
            </button>
          </section>
        )}

        {activeTab === 'status' && (
          <section className="teacher-card">
            <div className="teacher-status-header">
              <div>
                <h2>Student / device status</h2>
                <p className="hint">Filter by class above, then print the list for school admin payment.</p>
              </div>
              <button
                type="button"
                className="teacher-print-btn"
                onClick={handlePrintAssignmentList}
                disabled={loading || !students.length}
              >
                Print list
              </button>
            </div>

            <div className="teacher-status-list">
              {students.map((s) => {
                const hasDevice = Boolean(s.device_id);
                return (
                  <div key={s.id} className="teacher-status-card">
                    <div className="row">
                      <div>
                        <div className="name">{s.first_name} {s.last_name}</div>
                        <div className="meta">
                          Class {s.standard}-{s.division} · Roll {s.roll_number || '-'}
                          <br />
                          Device: {s.device_id || 'Not assigned'}
                        </div>
                      </div>
                      <span className={`teacher-badge ${hasDevice ? 'on' : 'off'}`}>
                        {hasDevice ? (s.device_status || 'Assigned') : 'No device'}
                      </span>
                    </div>
                  </div>
                );
              })}
              {!students.length && (
                <div className="teacher-empty">No students found for these filters</div>
              )}
            </div>
          </section>
        )}
      </main>

      <nav className="teacher-bottom-nav" aria-label="Teacher navigation">
        <button type="button" className={activeTab === 'assign' ? 'active' : ''} onClick={() => setActiveTab('assign')}>
          Assign
        </button>
        <button type="button" className={activeTab === 'status' ? 'active' : ''} onClick={openStatus}>
          Status
        </button>
      </nav>
    </div>
  );
};

export default TeacherDashboard;
